import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Shield, Smartphone, MessageSquare, Copy, Check, Eye, EyeOff, Info } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import { authAPI } from '../../services/api'
import TwoFactorGuideModal from '../../components/TwoFactorGuideModal'
import { QRCodeDisplay } from '../../utils/qrcode'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import CurrentFactorField, { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { currentCodeProblem, currentCodeMayBeSpent, currentFactorRefusal, normalizeCurrentCode, refusalSentence } from '../../utils/currentFactor'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

export default function TwoFactorSetup() {
  const navigate = useNavigate()
  const location = useLocation()
  const { pendingUserId, setUser, user } = useUserStore()
  const { t } = useI18n()

  // ★★ v242.87.0 (TF b35 #29) — "Change verification just logs me out."
  //   SecurityScreen and TwoFactorManageScreen both navigate here with only
  //   { resetup: true } and no userId. For a SIGNED-IN patient `pendingUserId`
  //   is null (cleared at sign-in), so `userId` was undefined and the guard
  //   below replaced the route with /signin. The token was never cleared and
  //   logout() was never called — it just looked exactly like a logout.
  //   Falling back to the authenticated user closes it for every caller,
  //   including ones not written yet. Do NOT "fix" this at the call sites.
  const userId = location.state?.userId || pendingUserId || user?.id
  const fromInvite = location.state?.fromInvite
  const isResetup = location.state?.resetup === true
  // PROD-1154: Settings ("Change verification method") passes the account's CURRENT method, only so that the
  //   field can offer "Text me a code" at once. The field itself never shows up front: the first setup call goes
  //   out without a code, and the field opens only when the API asks (403 2fa_current_factor_required, nothing
  //   used up). The live API (v243.369.0) never asks, so there the change goes through as before. The backup-code
  //   sign-in passes no method (its setupToken carries the proof); if the API asks anyway, the method is read.
  const currentMethod = location.state?.currentMethod
  
  const [step, setStep] = useState('choose') // 'choose' | 'totp-scan' | 'sms-verify' | 'confirm' | 'backup-codes'
  const [method, setMethod] = useState(null) // 'totp' | 'sms'
  const [qrUri, setQrUri] = useState('')
  const [totpSecret, setTotpSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [copiedCodes, setCopiedCodes] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [smsInfo, setSmsInfo] = useState(null)
  const [showGuide, setShowGuide] = useState(false)
  const [askCurrent, setAskCurrent] = useState(false)
  const [currentCode, setCurrentCode] = useState('')
  const [factorMethod, setFactorMethod] = useState(currentMethod === 'sms' || currentMethod === 'totp' ? currentMethod : null)
  const [lockSeconds, startLock] = useLockCountdown()
  const inputRef = useRef(null)

  useEffect(() => {
    if (!userId) {
      navigate('/signin', { replace: true })
    }
  }, [userId, navigate])

  useEffect(() => {
    if (step === 'confirm' || step === 'sms-verify') {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [step])

  // PROD-1154: a refusal of the current-factor check (by the API's `code`), or the sign-in lockout.
  //   Returns true when it handled the error. The field shows (and, for SMS, its send action once the
  //   account's method is known). 403 required needs no red line: the field's own line explains it.
  const handleFactorRefusal = (err) => {
    const refused = currentFactorRefusal(err)
    if (!refused) return false
    if (refused.locked) { startLock(refused.seconds); setAskCurrent(true); return true }
    setError(refusalSentence(t, refused.code))
    if (refused.ask) {
      setAskCurrent(true)
      if (!factorMethod) authAPI.get2faStatus().then((st) => { if (st && st.method) setFactorMethod(st.method) }).catch(() => {})
    }
    return true
  }

  const handleChooseMethod = async (chosen) => {
    // PROD-1154: no call without a code that can be one (a typo spends no attempt).
    //   Review L-1: an empty field gets no red line; the field's own grey line already says a code is needed.
    const problem = currentCodeProblem(askCurrent, currentCode)
    if (problem) { setError(refusalSentence(t, problem)); return }
    const sentCode = askCurrent ? normalizeCurrentCode(currentCode) : undefined
    setMethod(chosen)
    setError('')
    setLoading(true)
    
    try {
      const response = await authAPI.setup2fa(userId, chosen, sentCode)
      // PROD-1154: the returned setupToken carries the proof; a method switch or resend in this change asks no more.
      setAskCurrent(false)
      setCurrentCode('')
      
      if (chosen === 'totp') {
        setQrUri(response.qrUri)
        setTotpSecret(response.secret)
        setStep('totp-scan')
      } else {
        setSmsInfo(response)
        setStep('sms-verify')
      }
    } catch (err) {
      // ★★ v242.132.0: surface the reason. See utils/apiErrorMessage.js — the
      //    old `err.error ||` collapsed a 403, a 429, a 5xx and a dropped
      //    connection into one string, which is why a report of that string
      //    could not be diagnosed.
      console.error('[2FA] setup failed:', err?.status, err)
      // PROD-1154 (review L1): a code that may have been used up (a wrong one, a timeout, a 500 after it passed)
      //   is emptied, so a retry cannot spend an attempt on it.
      if (sentCode && currentCodeMayBeSpent(err)) setCurrentCode('')
      if (handleFactorRefusal(err)) return
      // PROD-807: a non-US/Canada number is refused - the server's sentence, no "(400)", and
      // the step stays on 'choose' (nothing was sent).
      setError(isSmsRefused(err) ? (smsRefusedSentence(err) || apiErrorMessage(err, t('errors2.failedToInitSetup'))) : apiErrorMessage(err, t('errors2.failedToInitSetup')))
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError(t('errors2.pleaseEnter6Digit'))
      return
    }
    
    setError('')
    setLoading(true)
    
    try {
      const response = await authAPI.verify2faSetup(userId, code, method)
      
      if (response.backupCodes) {
        setBackupCodes(response.backupCodes)
      }
      
      if (response.token && response.user) {
        // v242.169.0 TOKEN-KEY-1 — the second, raw write of this JWT is gone.
        // authAPI.verify2faSetup already stored it through setToken(), which is
        // the only path that respects the native/web storage policy and the only
        // one clearToken() can undo. What used to be written here was a live
        // bearer under a key nothing read and no logout cleared.
        setUser(response.user)
      }
      
      setStep('backup-codes')
    } catch (err) {
      console.error('[2FA] verify failed:', err?.status, err)
      setError(apiErrorMessage(err, t('twoFactor.invalidCode')))
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError('')
  }

  const handleCopyBackupCodes = () => {
    const text = backupCodes.join('\n')
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCodes(true)
      setTimeout(() => setCopiedCodes(false), 3000)
    }).catch(() => {
      // Fallback: select text
      const el = document.getElementById('backup-codes-text')
      if (el) {
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      }
    })
  }

  const handleFinish = () => {
    navigate('/home', { replace: true })
  }

  // v213.2 (C-A-178): Step number for the 3-dot progress indicator.
  //   1 = choose method, 2 = configure (TOTP scan / SMS code entry / TOTP confirm),
  //   3 = backup codes / done.
  const stepNumber = step === 'choose'
    ? 1
    : (step === 'backup-codes' ? 3 : 2)

  // v213.2 (C-A-178): Resend cooldown for SMS verification — gives the patient
  // a clear "didn't receive it?" path instead of silently waiting.
  const [smsResendCooldown, setSmsResendCooldown] = useState(0)
  const [smsResending, setSmsResending] = useState(false)

  useEffect(() => {
    if (step === 'sms-verify') setSmsResendCooldown(30)
  }, [step])

  useEffect(() => {
    if (smsResendCooldown <= 0) return
    const t = setTimeout(() => setSmsResendCooldown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [smsResendCooldown])

  const handleResendSms = async () => {
    if (smsResendCooldown > 0 || smsResending) return
    setSmsResending(true)
    setError('')
    try {
      const response = await authAPI.setup2fa(userId, 'sms')
      setSmsInfo(response)
      setSmsResendCooldown(30)
    } catch (err) {
      // ★★ v242.132.0: surface the reason. See utils/apiErrorMessage.js — the
      //    old `err.error ||` collapsed a 403, a 429, a 5xx and a dropped
      //    connection into one string, which is why a report of that string
      //    could not be diagnosed.
      console.error('[2FA] setup failed:', err?.status, err)
      // PROD-1154: the change's proof is gone (e.g. its setupToken expired): back to the choice, where the
      //   current-code field asks again. Nothing was sent, so no cooldown.
      if (handleFactorRefusal(err)) { setStep('choose'); return }
      // PROD-807: refused = nothing sent, so no cooldown is started (setSmsResendCooldown is
      // only reached on success above).
      setError(isSmsRefused(err) ? (smsRefusedSentence(err) || apiErrorMessage(err, t('errors2.failedToInitSetup'))) : apiErrorMessage(err, t('errors2.failedToInitSetup')))
    } finally {
      setSmsResending(false)
    }
  }

  // Code digit display
  const codeDigits = code.split('')

  if (!userId) return null

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 pt-6 md:px-10 md:pt-10 flex flex-col">
        {/* v213.2 (C-A-178): header now shows step indicator (1·2·3) for clarity */}
        <div className="flex items-center justify-between mb-2">
          {step !== 'backup-codes' ? (
            <button
              onClick={() => {
                if (step === 'confirm') setStep(method === 'totp' ? 'totp-scan' : 'sms-verify')
                else if (step === 'totp-scan' || step === 'sms-verify') setStep('choose')
                else navigate(-1)
              }}
              className="p-2 -ml-2 text-gray-600"
              aria-label="Back"
            >
              <ArrowLeft size={24} />
            </button>
          ) : (
            <div style={{ width: 40 }} />
          )}
          <div className="flex gap-1.5" aria-label={`Step ${stepNumber} of 3`}>
            {[1, 2, 3].map((n) => {
              const reached = stepNumber >= n
              const isFinal = n === 3 && step === 'backup-codes'
              return (
                <div
                  key={n}
                  style={{
                    width: 22, height: 4, borderRadius: 2,
                    background: isFinal ? '#66D170' : (reached ? '#46A8F9' : '#E2E8F0')
                  }}
                />
              )
            })}
          </div>
          <div style={{ width: 40 }} />
        </div>

        <div className="flex-1 flex flex-col items-center pt-6">

          {/* ===== STEP 1: Choose method ===== */}
          {step === 'choose' && (
            <>
              <div className="w-16 h-16 bg-orella-100 rounded-full flex items-center justify-center mb-5">
                <Shield className="w-8 h-8 text-orella-500" />
              </div>
              <h1 className="text-2xl font-bold text-orella-700 mb-2 text-center">
                {t('twoFactor.setUpSignInSecurity', 'Set up sign-in security')}
              </h1>
              <p className="text-gray-500 text-center mb-4 max-w-sm">
                {t('twoFactor.setUpBody', "One last step to keep your health information private. Choose how you'd like to confirm it's you each time you sign in.")}
              </p>

              {/* 2FA Setup Guide link */}
              <button
                onClick={() => setShowGuide(true)}
                className="text-orella-500 font-semibold text-[13px] mb-6 inline-flex items-center gap-1 underline decoration-orella-100 underline-offset-[3px] hover:decoration-orella-500 transition-colors"
              >
                <Info size={14} />
                {t('twoFactor.whatIsTwoFactor', 'What is two-factor authentication?')}
              </button>

              <TwoFactorGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} theme="blue" />

              {/* PROD-1154: the sign-in lockout replaces the error while it runs */}
              <div className="w-full max-w-sm">
                <LockNotice seconds={lockSeconds} />
              </div>

              {!lockSeconds && error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm w-full max-w-sm">
                  {error}
                </div>
              )}

              {/* PROD-1154: 2FA is on - a CURRENT code (or a backup code) before a new method is offered */}
              {askCurrent && (
                <div className="w-full max-w-sm mb-4">
                  <CurrentFactorField
                    value={currentCode}
                    onChange={(v) => { setCurrentCode(v); setError('') }}
                    method={factorMethod}
                    disabled={loading || !!lockSeconds}
                    autoFocus
                  />
                </div>
              )}

              <div className="w-full max-w-sm space-y-3">
                <button
                  onClick={() => handleChooseMethod('totp')}
                  disabled={loading || !!lockSeconds}
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl text-left hover:border-orella-500 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orella-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Smartphone className="w-5 h-5 text-orella-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{t('twoFactor.authenticatorApp')}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {t('twoFactor.totpOptionBody', 'Use Google Authenticator, Authy, or any TOTP app. Most secure option.')}
                      </p>
                      <span className="inline-block mt-2 text-xs font-medium text-orella-500 bg-orella-50 px-2 py-0.5 rounded-full">
                        {t('diagnose.recommended', 'Recommended')}
                      </span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleChooseMethod('sms')}
                  disabled={loading || !!lockSeconds}
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl text-left hover:border-orella-500 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{t('twoFactor.textMessageSms')}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {t('twoFactor.smsOptionBody', 'Receive a code by text each time you sign in. No extra app needed.')}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {loading && (
                <div className="mt-6 flex items-center gap-2 text-gray-500 text-sm">
                  <OrellaSpinner size={16} tone="inherit" />
                  {t('security.settingUp', 'Setting up...')}
                </div>
              )}
              {/* PROD-1194 review L2: this step has no bar, so it keeps a bottom padding of its own: the
                  column's old 24 px (40 px from md), or the home-indicator inset when that is larger. */}
              <div aria-hidden data-choose-end className="bottom-actions [--ba-min:24px] md:[--ba-min:40px]" />
            </>
          )}

          {/* ===== STEP 2A: TOTP — Scan QR ===== */}
          {step === 'totp-scan' && (
            <>
              <h1 className="text-2xl font-bold text-orella-700 mb-2 text-center">
                {t('twoFactor.scanQrCode', 'Scan QR code')}
              </h1>
              <p className="text-gray-500 text-center mb-6 max-w-sm">
                {t('twoFactor.scanInstructions')}
              </p>

              {/* v242.87.0 — QR generated LOCALLY. This used to be an <img> whose
                  src put `qrUri` — the otpauth:// URI, which contains the TOTP
                  shared secret — into a query string to api.qrserver.com, a third
                  party with no BAA. Same defect and same fix as
                  OnboardingSecurity.jsx; see the long note there. The otpauth
                  anchor below is TF b35 #24: the phone showing the QR is often
                  the phone meant to scan it. */}
              {qrUri && (
                <div className="bg-white p-4 rounded-2xl border border-gray-200 mb-4">
                  <QRCodeDisplay value={qrUri} size={192} color="#15354F" />
                </div>
              )}
              {qrUri && (
                <a href={qrUri} data-otpauth-link="1" className="text-sm font-semibold text-orella-500 mb-4">
                  {t('twoFactor.openInApp', 'Setting up on this phone? Open my authenticator app')}
                </a>
              )}

              {/* Manual entry fallback */}
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="text-sm text-orella-500 mb-4 flex items-center gap-1"
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                {showSecret ? t('twoFactor.hideSetupKey') : t('twoFactor.showSetupKey')} {t('twoFactor.setupKey')}
              </button>
              
              {showSecret && totpSecret && (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-4 flex items-center gap-2">
                  <code className="text-sm font-mono text-gray-700 select-all">{totpSecret}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(totpSecret)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              )}

              <p className="text-sm text-gray-400 mb-4">
                {t('twoFactor.dontHaveApp')}{' '}
                <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener" className="text-orella-500 underline">{t('twoFactor.googleAuthenticator', 'Google Authenticator')}</a>
                {' '}or{' '}
                <a href="https://authy.com/download/" target="_blank" rel="noopener" className="text-orella-500 underline">{t('twoFactor.authy', 'Authy')}</a>.
              </p>

              {/* PROD-1194: each step's primary is pinned at the bottom (resend, where there is one, under it). */}
              <BottomActionBar className="self-stretch -mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center">
              <button
                onClick={() => { setStep('confirm'); setCode('') }}
                className="w-full max-w-sm py-4 bg-orella-500 text-white font-semibold rounded-full breathe-blue"
              >
                I've scanned the code
              </button>
              </BottomActionBar>
            </>
          )}

          {/* ===== STEP 2B: SMS — Code sent ===== */}
          {step === 'sms-verify' && (
            <>
              <h1 className="text-2xl font-bold text-orella-700 mb-2 text-center">
                {t('twoFactor.confirmYourPhoneNumber', 'Confirm your phone number')}
              </h1>
              <p className="text-gray-500 text-center mb-1 max-w-sm">
                {t('auth.codeSentTo')}
              </p>
              {smsInfo?.maskedPhone ? (
                <p className="text-base font-semibold text-orella-700 mb-6">{smsInfo.maskedPhone}</p>
              ) : (
                <p className="text-sm text-gray-400 mb-6">your phone</p>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm w-full max-w-sm">
                  {error}
                </div>
              )}

              {/* Code input */}
              <div className="relative mb-6">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={handleCodeChange}
                  maxLength={6}
                  className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
                />
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div
                      key={index}
                      className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold border-2 rounded-xl transition-colors ${
                        index === code.length 
                          ? 'border-orella-500 bg-orella-50' 
                          : codeDigits[index] 
                            ? 'border-orella-300 bg-white' 
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      {codeDigits[index] || ''}
                    </div>
                  ))}
                </div>
              </div>

              <BottomActionBar className="self-stretch -mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center">
              <button
                onClick={handleVerifyCode}
                disabled={loading || code.length !== 6}
                className="w-full max-w-sm py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 breathe-blue"
              >
                {loading ? t('twoFactor.verifying') : 'Verify & continue'}
              </button>

              {/* v213.2 (C-A-178): Resend with cooldown */}
              <div className="mt-4 text-sm text-gray-500">
                Didn't receive it?{' '}
                {smsResendCooldown > 0 ? (
                  <span className="text-gray-400">
                    {t('twoFactor.resendIn', { seconds: String(smsResendCooldown).padStart(2, '0') })}
                  </span>
                ) : (
                  <button
                    onClick={handleResendSms}
                    disabled={smsResending}
                    className="text-orella-500 font-semibold disabled:opacity-50"
                  >
                    {smsResending ? 'Sending...' : 'Resend code'}
                  </button>
                )}
              </div>
              </BottomActionBar>
            </>
          )}

          {/* ===== STEP 3: Confirm TOTP code ===== */}
          {step === 'confirm' && method === 'totp' && (
            <>
              <h1 className="text-2xl font-bold text-orella-700 mb-2 text-center">
                {t('twoFactor.confirmSetup', 'Confirm setup')}
              </h1>
              <p className="text-gray-500 text-center mb-6 max-w-sm">
                {t('twoFactor.enterCodeToConfirm')}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm w-full max-w-sm">
                  {error}
                </div>
              )}

              <div className="relative mb-6">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={handleCodeChange}
                  maxLength={6}
                  className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
                />
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div
                      key={index}
                      className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold border-2 rounded-xl transition-colors ${
                        index === code.length 
                          ? 'border-orella-500 bg-orella-50' 
                          : codeDigits[index] 
                            ? 'border-orella-300 bg-white' 
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      {codeDigits[index] || ''}
                    </div>
                  ))}
                </div>
              </div>

              <BottomActionBar className="self-stretch -mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center">
              <button
                onClick={handleVerifyCode}
                disabled={loading || code.length !== 6}
                className="w-full max-w-sm py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 breathe-blue"
              >
                {loading ? t('twoFactor.verifying') : 'Verify & continue'}
              </button>
              </BottomActionBar>
            </>
          )}

          {/* ===== STEP 4: Backup codes ===== */}
          {step === 'backup-codes' && (
            <>
              {/* v213.2: brand green (#66D170) instead of generic tailwind green */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: 'rgba(102,209,112,0.15)' }}
              >
                <Check className="w-8 h-8" style={{ color: '#66D170' }} />
              </div>
              <h1 className="text-2xl font-bold text-orella-700 mb-2 text-center">
                {t('twoFactor.saveYourBackupCodes', 'Save your backup codes')}
              </h1>
              <p className="text-gray-500 text-center mb-6 max-w-sm">
                {method === 'totp' ? t('twoFactor.lostAccessTotp') : t('twoFactor.lostAccessPhone')}
              </p>

              <div className="w-full max-w-sm bg-gray-50 rounded-2xl p-5 mb-4">
                <div id="backup-codes-text" className="grid grid-cols-2 gap-2">
                  {backupCodes.map((c, i) => (
                    <div key={i} className="font-mono text-sm text-gray-700 bg-white px-3 py-2 rounded-lg text-center border border-gray-200">
                      {c}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCopyBackupCodes}
                className="flex items-center gap-2 text-orella-500 font-medium text-sm mb-6"
              >
                {copiedCodes ? <Check size={16} /> : <Copy size={16} />}
                {copiedCodes ? t('twoFactor.copied') : t('twoFactor.copyAllCodes')}
              </button>

              <div className="w-full max-w-sm p-3 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                <p className="text-amber-700 text-sm">
                  {t('twoFactor.backupCodesBody', "Store these codes somewhere safe. You won't be able to see them again after leaving this screen.")}
                </p>
              </div>

              <BottomActionBar className="self-stretch -mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center">
              <button
                onClick={handleFinish}
                className="w-full max-w-sm py-4 bg-orella-500 text-white font-semibold rounded-full breathe-blue"
              >
                I've saved my codes — continue
              </button>
              </BottomActionBar>
            </>
          )}
        </div>
      </div>
    </ResponsiveWrapper>
  )
}
